// The counts of the lasting effects that the seals and the bands show. The
// state changes at once. The counts change when the beat that tells the change
// starts: the effect beat of the cast, or the event of a turn that passes or of
// an effect that ends. `narrator.ts` gives each beat the counts to show. A
// change that has no beat, such as a roll that uses a discount, shows when the
// board is quiet.

import { type LastingCounts, lastingCountsOf, lastingSignature } from '../game/lasting';
import type { GameState } from '../game/state';
import { createStore } from '../lib/store';
import { boardCoverStore } from './boardCover';
import { beatStore, isQuiet, queueStore } from './director';
import { gameStore } from './game';
import { historyStore, isReviewing } from './history';
import { matchWaitStore } from './matchWait';
import { stageStore } from './stage';
import { viewedGame } from './view';

export const lastingShownStore = createStore<LastingCounts>(lastingCountsOf(gameStore.get()));

/** Shows the counts of `state` now. A set with the same counts redraws nothing. */
export const showLastingOf = (state: GameState): void => {
  const counts = lastingCountsOf(state);
  if (lastingSignature(counts) === lastingSignature(lastingShownStore.get())) return;
  lastingShownStore.set(counts);
};

/** The counts a view draws: those of the step the history shows, or those of the last beat. */
export const shownLasting = (): LastingCounts =>
  isReviewing() ? lastingCountsOf(viewedGame()) : lastingShownStore.get();

/** Calls the listener when the counts a view draws can change. */
export const subscribeLasting = (listener: () => void): void => {
  lastingShownStore.subscribe(listener);
  historyStore.subscribe(listener);
};

/** Call once at startup. */
export const startLastingShown = (): void => {
  // After the narrator queues the beats of a change, so a change with a beat waits for it.
  const showWhenQuiet = (): void =>
    queueMicrotask(() => {
      if (isQuiet()) showLastingOf(gameStore.get());
    });
  for (const store of [
    gameStore,
    beatStore,
    queueStore,
    stageStore,
    boardCoverStore,
    matchWaitStore,
  ])
    store.subscribe(showWhenQuiet);
};
